import { codeFont } from '../../utils/fonts';
import * as React from 'react';
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Tooltip,
  Badge,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LinkIcon from '@mui/icons-material/Link';
import EmailIcon from '@mui/icons-material/Email';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import EditIcon from '@mui/icons-material/Edit';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import HistoryIcon from '@mui/icons-material/History';
import InventoryIcon from '@mui/icons-material/Inventory';
import TagIcon from '@mui/icons-material/Tag';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import FactoryIcon from '@mui/icons-material/Factory';
import TableChartIcon from '@mui/icons-material/TableChart';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PrintIcon from '@mui/icons-material/Print';
import { withTranslation } from 'react-i18next';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import AddPartDialog from '../maintenances/AddPartDialog';
import PartEditDialog from './PartEditDialog';
import PartDetailDialog from './PartDetailDialog';
import PartAuditLogDialog from './PartAuditLogDialog';
import EnableInventoryDialog from './EnableInventoryDialog';
import PrintPartLabelsDialog from './PrintPartLabelsDialog';
import { useUserStore } from 'store';
import { getCsrfToken, formatQuantity } from '../../utils';
import { usePartCodeOnly } from '../../utils/partSearchPrefs';
import {
  useListPreferences,
  FilterButton,
  FilterPanel,
  FilterSection,
  FilterChip,
  SearchInput,
  FilterSummary,
} from '../shared/ListFilters';
import DataTable from '../shared/DataTable';
import { usePaginatedList } from '../../hooks/usePaginatedList';
import PaginationControls from '../shared/PaginationControls';

import { can } from '../../utils/access';
const STORAGE_KEY = 'parts_list_preferences';

const defaultPreferences = {
  sortField: 'name',
  sortDirection: 'asc',
  filters: {
    sourceType: 'all', // all, manual, email, file_upload, url_parse
    hasManufacturer: 'all', // all, yes, no
    hasPrice: 'all', // all, yes, no
  },
  searchQuery: '',
};

const sourceIcons = {
  manual: <EditIcon fontSize="small" />,
  email: <EmailIcon fontSize="small" />,
  file_upload: <UploadFileIcon fontSize="small" />,
  url_parse: <LinkIcon fontSize="small" />,
  csv_import: <TableChartIcon fontSize="small" />,
};

const sourceColors = {
  manual: 'default',
  email: 'info',
  file_upload: 'success',
  url_parse: 'secondary',
  csv_import: 'warning',
};

const sourceLabels = {
  manual: 'Manual',
  email: 'Email',
  file_upload: 'File',
  url_parse: 'URL',
  csv_import: 'CSV Import',
};

const MainPartsView = ({ t }) => {
  const { currentOrganization } = useUserStore();
  const inventoryEnabled = currentOrganization?.inventory_tracking_enabled ?? false;
  const showInclVat = currentOrganization?.default_price_includes_vat ?? false;
  const cataloguesEnabled =
    (currentOrganization?.vendor_catalogues_enabled ?? false) &&
    can(currentOrganization, 'catalogues.manage');
  const [driftCount, setDriftCount] = useState(0);

  useEffect(() => {
    if (!cataloguesEnabled) return;
    axios
      .get('/api/parts/price-drift/', { params: { count_only: 'true' } })
      .then((res) => setDriftCount(res.data.count || 0))
      .catch(() => {});
  }, [cataloguesEnabled]);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [enableInventoryDialogOpen, setEnableInventoryDialogOpen] = useState(false);
  const [selectedPart, setSelectedPart] = useState(null);
  // Rows ticked for label printing, and what the label dialog is open for: a list of
  // part ids, or 'selection' for the ticked rows. DataTable keeps selectionActions
  // from its first render (memo compares labels only), so the action must not
  // capture the selection; it is read here at render time instead.
  const [selectedPartIds, setSelectedPartIds] = useState(new Set());
  const [labelPartIds, setLabelPartIds] = useState(null);
  const labelDialogPartIds = labelPartIds === 'selection' ? Array.from(selectedPartIds) : labelPartIds || [];
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuPart, setMenuPart] = useState(null);
  const [pendingEmailCount, setPendingEmailCount] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [partToDelete, setPartToDelete] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep link from the global search (Ctrl+K): /parts?part=<id>. The detail dialog takes a
  // full part object, so fetch it by id (same serializer as the list), then drop the param.
  useEffect(() => {
    const partId = searchParams.get('part');
    if (!partId) return;
    const next = new URLSearchParams(searchParams);
    next.delete('part');
    setSearchParams(next, { replace: true });
    let cancelled = false;
    axios
      .get(`/api/parts/${partId}/`)
      .then((response) => {
        if (cancelled) return;
        setSelectedPart(response.data);
        setDetailDialogOpen(true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('part')]);

  const {
    preferences,
    updatePreferences,
    updateFilters: updateLocalFilters,
    resetFilters: resetLocalFilters,
    handleSort: handleLocalSort,
    filtersOpen,
    toggleFiltersOpen,
  } = useListPreferences(STORAGE_KEY, defaultPreferences);

  // Code-only search is shared with the part pickers, so it lives in its own
  // org-scoped key rather than this view's preference blob.
  const [codeOnly, setCodeOnly] = usePartCodeOnly();

  // Build server-side filter params from local preferences
  const serverFilters = useMemo(() => {
    const filters = {};

    // Code-only search (shared preference, also used by the part pickers)
    if (codeOnly) {
      filters.code_only = true;
    }

    // Source type filter
    if (preferences.filters.sourceType !== 'all') {
      filters.source_type = preferences.filters.sourceType;
    }

    // Has manufacturer filter
    if (preferences.filters.hasManufacturer === 'yes') {
      filters.has_manufacturer = true;
    } else if (preferences.filters.hasManufacturer === 'no') {
      filters.has_manufacturer = false;
    }

    // Has price filter
    if (preferences.filters.hasPrice === 'yes') {
      filters.has_price = true;
    } else if (preferences.filters.hasPrice === 'no') {
      filters.has_price = false;
    }

    return filters;
  }, [preferences.filters, codeOnly]);

  // Map local sort field to server field
  const serverSortField = useMemo(() => {
    const sortMap = {
      name: 'name',
      manufacturer: 'manufacturer_name',
      code: 'manufacturer_code',
      price: 'price',
      sell_price: 'sell_price',
    };
    return sortMap[preferences.sortField] || 'name';
  }, [preferences.sortField]);

  // Use paginated list hook for server-side data fetching
  // Pass initial values from localStorage to prevent double request on mount
  const {
    data: parts,
    loading,
    error,
    page,
    pageSize,
    totalCount,
    totalPages,
    setPage,
    setPageSize,
    searchQuery,
    setSearchQuery,
    setFilters: setServerFilters,
    setSort,
    refetch,
  } = usePaginatedList('/api/parts/', {
    initialFilters: serverFilters,
    defaultSortField: serverSortField,
    defaultSortDirection: preferences.sortDirection,
    initialSearchQuery: preferences.searchQuery || '',
  });

  // Sync local preferences to server filters
  useEffect(() => {
    setServerFilters(serverFilters);
  }, [serverFilters, setServerFilters]);

  // Sync sort from local preferences to server
  useEffect(() => {
    setSort(serverSortField, preferences.sortDirection);
  }, [serverSortField, preferences.sortDirection, setSort]);

  // Handle sort - update local preferences (which triggers server sort via effect)
  const handleSort = useCallback((field) => {
    handleLocalSort(field);
  }, [handleLocalSort]);

  // Update filters in local preferences (which triggers server filter via effect)
  const updateFilters = useCallback((newFilters) => {
    updateLocalFilters(newFilters);
  }, [updateLocalFilters]);

  // Reset all filters
  const resetFilters = useCallback(() => {
    resetLocalFilters();
  }, [resetLocalFilters]);

  // Sync search query from preferences to hook
  useEffect(() => {
    setSearchQuery(preferences.searchQuery);
  }, [preferences.searchQuery, setSearchQuery]);

  const fetchPendingEmailCount = useCallback(async () => {
    try {
      const response = await axios.get('/api/parts/email-suggestions/');
      const count = response.data.reduce((sum, event) => sum + event.pending_count, 0);
      setPendingEmailCount(count);
    } catch (err) {
      console.error('Error fetching email suggestions:', err);
    }
  }, []);

  useEffect(() => {
    fetchPendingEmailCount();
  }, [fetchPendingEmailCount]);

  const handleMenuOpen = (event, part) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuPart(part);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuPart(null);
  };

  const handleRowClick = (part) => {
    setSelectedPart(part);
    setDetailDialogOpen(true);
  };

  const handleEdit = () => {
    setSelectedPart(menuPart);
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleDelete = () => {
    if (menuPart) {
      setPartToDelete(menuPart);
      setDeleteDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleEnableInventory = () => {
    setSelectedPart(menuPart);
    setEnableInventoryDialogOpen(true);
    handleMenuClose();
  };

  const handleDisableInventory = async () => {
    if (!menuPart) return;
    try {
      await axios.post(
        `/api/parts/${menuPart.id}/disable-inventory/?organization=${currentOrganization.organization_id}`,
        {},
        {
          headers: {
            'X-CSRFToken': getCsrfToken(),
          },
        }
      );
      refetch();
    } catch (err) {
      console.error('Error disabling inventory:', err);
    }
    handleMenuClose();
  };

  const confirmDelete = async () => {
    if (partToDelete) {
      try {
        await axios.delete(`/api/parts/${partToDelete.id}/`);
        refetch(); // Refresh the list after deletion
      } catch (err) {
        console.error('Error deleting part:', err);
      }
    }
    setDeleteDialogOpen(false);
    setPartToDelete(null);
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setPartToDelete(null);
  };

  const handlePartCreated = (newParts) => {
    // Refresh parts and pending count
    refetch();
    fetchPendingEmailCount();
  };

  const handlePartUpdated = (updatedPart) => {
    // Refresh the list to get updated data from server
    refetch();
  };

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (preferences.filters.sourceType !== 'all') count++;
    if (preferences.filters.hasManufacturer !== 'all') count++;
    if (preferences.filters.hasPrice !== 'all') count++;
    return count;
  }, [preferences.filters]);

  // Build filter summary chips
  const filterSummaryChips = useMemo(() => {
    const chips = [];

    if (preferences.filters.sourceType !== 'all') {
      chips.push({ key: 'source', label: t(sourceLabels[preferences.filters.sourceType] || preferences.filters.sourceType) });
    }

    if (preferences.filters.hasManufacturer === 'yes') {
      chips.push({ key: 'manufacturer', label: t('Has manufacturer') });
    } else if (preferences.filters.hasManufacturer === 'no') {
      chips.push({ key: 'manufacturer', label: t('No manufacturer') });
    }

    if (preferences.filters.hasPrice === 'yes') {
      chips.push({ key: 'price', label: t('Has price') });
    } else if (preferences.filters.hasPrice === 'no') {
      chips.push({ key: 'price', label: t('No price') });
    }

    return chips;
  }, [preferences.filters, t]);

  // Parts are now filtered and sorted on the server
  // The 'parts' variable from usePaginatedList already contains the filtered/sorted data

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '-';
    return new Intl.NumberFormat('fi-FI', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  // State for error snackbar - cleared when error changes to null
  const [showErrorSnackbar, setShowErrorSnackbar] = React.useState(false);
  const prevError = React.useRef(error);

  React.useEffect(() => {
    // Show snackbar when a new error appears
    if (error && error !== prevError.current) {
      setShowErrorSnackbar(true);
    }
    prevError.current = error;
  }, [error]);

  const handleCloseErrorSnackbar = () => {
    setShowErrorSnackbar(false);
  };

  return (
    <Box data-testid="parts-page">
      {/* Error Snackbar - shows error without blocking the UI */}
      <Snackbar
        open={showErrorSnackbar && !!error}
        autoHideDuration={6000}
        onClose={handleCloseErrorSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseErrorSnackbar} severity="error" sx={{ width: '100%' }}>
          {t('Error')}: {error}
        </Alert>
      </Snackbar>
      {/* Header - Responsive */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            component="h4"
            variant="h4"
            data-testid="parts-heading"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            {t('Parts')}
          </Typography>
          {cataloguesEnabled && driftCount > 0 && (
            <Chip
              label={t('catalogues.driftBadge', { count: driftCount })}
              color="warning"
              variant="outlined"
              size="small"
              component={Link}
              to="/catalogues/price-drift"
              clickable
              data-testid="parts-drift-chip"
              sx={{ mt: 0.5 }}
            />
          )}
          <Typography
            sx={{
              fontSize: '14px',
              color: 'text.secondary',
              mt: 0.5,
              display: { xs: 'none', sm: 'block' },
            }}
          >
            {t('Manage your parts inventory and extract data from any source')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Tooltip title={t('Activity Log')}>
            <IconButton
              onClick={() => setAuditLogOpen(true)}
              sx={{
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              <HistoryIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            onClick={() => {
              setSelectedPart(null);
              setAddDialogOpen(true);
            }}
            data-testid="add-part-button"
            startIcon={
              pendingEmailCount > 0 ? (
                <Badge badgeContent={pendingEmailCount} color="error">
                  <AddIcon />
                </Badge>
              ) : (
                <AddIcon />
              )
            }
            sx={{
              bgcolor: 'secondary.main',
              color: 'white',
              px: 2.5,
              py: 1.5,
              borderRadius: '10px',
              fontWeight: 500,
              fontSize: '14px',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              boxShadow: '0 4px 12px rgba(49, 130, 206, 0.3)',
              flex: { xs: 1, sm: 'none' },
              '&:hover': {
                bgcolor: 'secondary.light',
                boxShadow: '0 6px 16px rgba(49, 130, 206, 0.4)',
              },
            }}
          >
            {t('Add Part')}
          </Button>
        </Box>
      </Box>

      {/* Pending Email Imports Alert */}
      {pendingEmailCount > 0 && (
        <Alert
          severity="info"
          icon={<EmailIcon />}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setAddDialogOpen(true)}
            >
              {t('View')}
            </Button>
          }
          sx={{ mb: 2.5, borderRadius: '10px', alignItems: 'center', '& .MuiAlert-action': { pt: 0, mt: 0 } }}
        >
          {t('You have {{count}} pending email imports', { count: pendingEmailCount })}
        </Alert>
      )}

      {/* Search and Filter Row */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 2.5,
          alignItems: { xs: 'stretch', sm: 'center' },
        }}
      >
        <SearchInput
          value={preferences.searchQuery}
          onChange={(value) => updatePreferences({ searchQuery: value })}
          onClear={() => updatePreferences({ searchQuery: '' })}
          placeholder={codeOnly ? t('partSearch.codeOnlyPlaceholder') : t('Search by name, code, manufacturer...')}
        />
        <Tooltip title={t('partSearch.codeOnlyHint')}>
          <span>
            <FilterChip
              icon={<TagIcon />}
              label={t('partSearch.codeOnly')}
              active={codeOnly}
              onClick={() => setCodeOnly(!codeOnly)}
              color="primary"
              data-testid="parts-search-code-only"
            />
          </span>
        </Tooltip>
        <FilterButton
          t={t}
          activeCount={activeFilterCount}
          isOpen={filtersOpen}
          onClick={toggleFiltersOpen}
        />
      </Box>

      {/* Active Filter Summary */}
      <FilterSummary chips={filterSummaryChips} onClear={resetFilters} t={t} />

      {/* Filter Panel */}
      <FilterPanel
        open={filtersOpen}
        resultCount={totalCount}
        onReset={resetFilters}
        resetDisabled={activeFilterCount === 0}
        t={t}
      >
        <FilterSection title={t('Source Type')}>
          <FilterChip
            icon={<EditIcon />}
            label={t('Manual')}
            active={preferences.filters.sourceType === 'manual'}
            onClick={() => updateFilters({ sourceType: preferences.filters.sourceType === 'manual' ? 'all' : 'manual' })}
            color="default"
          />
          <FilterChip
            icon={<EmailIcon />}
            label={t('Email')}
            active={preferences.filters.sourceType === 'email'}
            onClick={() => updateFilters({ sourceType: preferences.filters.sourceType === 'email' ? 'all' : 'email' })}
            color="info"
          />
          <FilterChip
            icon={<UploadFileIcon />}
            label={t('File')}
            active={preferences.filters.sourceType === 'file_upload'}
            onClick={() => updateFilters({ sourceType: preferences.filters.sourceType === 'file_upload' ? 'all' : 'file_upload' })}
            color="success"
          />
          <FilterChip
            icon={<LinkIcon />}
            label={t('URL')}
            active={preferences.filters.sourceType === 'url_parse'}
            onClick={() => updateFilters({ sourceType: preferences.filters.sourceType === 'url_parse' ? 'all' : 'url_parse' })}
            color="secondary"
          />
        </FilterSection>

        <FilterSection title={t('Quick Filters')} dividerTop>
          <FilterChip
            icon={<FactoryIcon />}
            label={t('Has manufacturer')}
            active={preferences.filters.hasManufacturer === 'yes'}
            onClick={() => updateFilters({ hasManufacturer: preferences.filters.hasManufacturer === 'yes' ? 'all' : 'yes' })}
            color="primary"
          />
          <FilterChip
            icon={<FactoryIcon />}
            label={t('No manufacturer')}
            active={preferences.filters.hasManufacturer === 'no'}
            onClick={() => updateFilters({ hasManufacturer: preferences.filters.hasManufacturer === 'no' ? 'all' : 'no' })}
            color="warning"
          />
          <FilterChip
            icon={<AttachMoneyIcon />}
            label={t('Has price')}
            active={preferences.filters.hasPrice === 'yes'}
            onClick={() => updateFilters({ hasPrice: preferences.filters.hasPrice === 'yes' ? 'all' : 'yes' })}
            color="success"
          />
          <FilterChip
            icon={<AttachMoneyIcon />}
            label={t('No price')}
            active={preferences.filters.hasPrice === 'no'}
            onClick={() => updateFilters({ hasPrice: preferences.filters.hasPrice === 'no' ? 'all' : 'no' })}
            color="warning"
          />
        </FilterSection>
      </FilterPanel>

      {showInclVat && (
        <Chip
          size="small"
          icon={<AttachMoneyIcon />}
          label={t('Prices include VAT')}
          color="info"
          variant="outlined"
          sx={{ mb: 1 }}
        />
      )}

      {/* Desktop Table */}
      <DataTable
        columns={[
          {
            key: 'name',
            label: t('Part'),
            sortable: true,
            maxWidth: 400,
            render: (part) => (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary' }}>
                    {part.name}
                  </Typography>
                  {part.stock_info && (
                    <Tooltip
                      title={t('inventory.stockBreakdown', {
                        inStock: formatQuantity(part.stock_info.quantity_in_stock),
                        reserved: formatQuantity(part.stock_info.quantity_reserved || '0'),
                        free: formatQuantity(part.stock_info.quantity_free || part.stock_info.quantity_in_stock),
                      })}
                      arrow
                    >
                      <Chip
                        size="small"
                        label={formatQuantity(part.stock_info.quantity_in_stock)}
                        icon={
                          part.stock_info.is_negative ? <ErrorOutlineIcon /> :
                          part.stock_info.is_low_stock ? <WarningAmberIcon /> : null
                        }
                        color={
                          part.stock_info.is_negative ? 'error' :
                          part.stock_info.is_low_stock ? 'warning' : 'default'
                        }
                        variant="outlined"
                        sx={{ height: 20, fontSize: '11px' }}
                      />
                    </Tooltip>
                  )}
                </Box>
                {part.description && (
                  <Typography
                    sx={{
                      fontSize: '13px',
                      color: 'text.secondary',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '400px',
                    }}
                  >
                    {part.description}
                  </Typography>
                )}
              </Box>
            ),
          },
          {
            key: 'manufacturer',
            label: t('Manufacturer'),
            sortable: true,
            render: (part) => (
              <Typography sx={{ fontSize: '13px', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {part.manufacturer_name || '-'}
              </Typography>
            ),
          },
          {
            key: 'code',
            label: t('Code'),
            sortable: true,
            maxWidth: 150,
            render: (part) => {
              const searchLower = (preferences.searchQuery || '').toLowerCase().replace(/[\s\-\./\\]/g, '');
              const displayCode = part.primary_code || part.manufacturer_code;
              const matchedIdentifier = searchLower && part.identifiers
                ? part.identifiers.find((id) =>
                    id.code_normalized.toLowerCase().includes(searchLower) &&
                    id.code !== displayCode
                  )
                : null;
              return (
                <Box>
                  <Typography sx={{ fontSize: '13px', ...codeFont, color: 'primary.main' }}>
                    {displayCode || '-'}
                  </Typography>
                  {matchedIdentifier && (
                    <Typography sx={{ fontSize: '11px', color: 'text.secondary', fontStyle: 'italic' }}>
                      {t('identifier_found_by_code', { code: matchedIdentifier.code })}
                    </Typography>
                  )}
                </Box>
              );
            },
          },
          {
            key: 'price',
            label: t('Purchase price'),
            sortable: true,
            align: 'right',
            width: '1%',
            noWrap: true,
            render: (part) => (
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary' }}>
                  {part.is_margin_vat_scheme
                    ? formatPrice(part.margin_vat_purchase_price)
                    : formatPrice(showInclVat ? (part.price_incl_vat ?? part.price) : part.price)}
                </Typography>
                {part.is_margin_vat_scheme && (
                  <Chip
                    size="small"
                    label={t('Margin VAT')}
                    sx={{ height: 18, fontSize: '10px', mt: 0.5 }}
                    color="info"
                    variant="outlined"
                  />
                )}
              </Box>
            ),
          },
          {
            key: 'sell_price',
            label: t('Sell price'),
            sortable: true,
            align: 'right',
            width: '1%',
            noWrap: true,
            render: (part) => (
              <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary' }}>
                {formatPrice(showInclVat ? (part.sell_price_incl_vat ?? part.sell_price) : part.sell_price)}
              </Typography>
            ),
          },
          {
            key: 'actions',
            label: '',
            width: '1%',
            render: (part) => (
              <IconButton
                onClick={(e) => handleMenuOpen(e, part)}
                sx={{ color: 'text.disabled', '&:hover': { color: 'text.primary' } }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            ),
          },
        ]}
        data={parts}
        loading={loading}
        onRowClick={handleRowClick}
        selectable
        selectedIds={selectedPartIds}
        onSelectionChange={setSelectedPartIds}
        selectionActions={[
          {
            label: t('partLabels.printLabels'),
            icon: <PrintIcon fontSize="small" />,
            onClick: () => setLabelPartIds('selection'),
            variant: 'contained',
          },
        ]}
        sortField={preferences.sortField}
        sortDirection={preferences.sortDirection}
        onSort={handleSort}
        emptyIcon={<AutoFixHighIcon />}
        emptyTitle={preferences.searchQuery || activeFilterCount > 0 ? t('No parts match your criteria') : t('No parts yet')}
        emptyDescription={t('Add parts manually or extract them from URLs, files, or emails')}
        testIdPrefix="part-row"
        tableTestId="parts-table"
        pagination={{
          page,
          totalPages,
          totalCount,
          pageSize,
          onPageChange: setPage,
          onPageSizeChange: setPageSize,
        }}
      />

      {/* Mobile Cards - Hidden on desktop */}
      <Box sx={{ display: { xs: 'block', lg: 'none' }, position: 'relative' }}>
        {/* Loading overlay for refetch (when we have existing data) */}
        {loading && parts.length > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              pt: 8,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}
        {loading && parts.length === 0 ? (
          <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : parts.length === 0 ? (
          <Paper elevation={0} sx={{ p: 4, textAlign: 'center' }}>
            <AutoFixHighIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography color="text.secondary" sx={{ mb: 1 }}>
              {preferences.searchQuery || activeFilterCount > 0
                ? t('No parts match your criteria')
                : t('No parts yet')}
            </Typography>
            <Typography variant="body2" color="text.disabled">
              {t('Add parts manually or extract them from URLs, files, or emails')}
            </Typography>
          </Paper>
        ) : (
          parts.map((part) => (
            <Paper
              key={part.id}
              elevation={0}
              onClick={() => handleRowClick(part)}
              sx={{
                p: 2,
                mb: 1.5,
                cursor: 'pointer',
                transition: 'background-color 0.15s ease',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', gap: 1.5, flex: 1, minWidth: 0 }}>
                  {/* Part Icon */}
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '10px',
                      bgcolor: 'rgba(49, 130, 206, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'secondary.main',
                      flexShrink: 0,
                    }}
                  >
                    <InventoryIcon />
                  </Box>

                  {/* Part Info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                      <Typography
                        sx={{
                          fontSize: '15px',
                          fontWeight: 600,
                          color: 'text.primary',
                        }}
                      >
                        {part.name}
                      </Typography>
                      {part.stock_info && (
                        <Tooltip
                          title={t('inventory.stockBreakdown', {
                            inStock: formatQuantity(part.stock_info.quantity_in_stock),
                            reserved: formatQuantity(part.stock_info.quantity_reserved || '0'),
                            free: formatQuantity(part.stock_info.quantity_free || part.stock_info.quantity_in_stock),
                          })}
                          arrow
                        >
                          <Chip
                            size="small"
                            label={formatQuantity(part.stock_info.quantity_in_stock)}
                            icon={
                              part.stock_info.is_negative ? <ErrorOutlineIcon /> :
                              part.stock_info.is_low_stock ? <WarningAmberIcon /> : null
                            }
                            color={
                              part.stock_info.is_negative ? 'error' :
                              part.stock_info.is_low_stock ? 'warning' : 'default'
                            }
                            variant="outlined"
                            sx={{ height: 18, fontSize: '10px' }}
                          />
                        </Tooltip>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                      {(part.primary_code || part.manufacturer_code) && (
                        <Typography
                          sx={{
                            fontSize: '12px',
                            ...codeFont,
                            color: 'primary.main',
                          }}
                        >
                          {part.primary_code || part.manufacturer_code}
                        </Typography>
                      )}
                    </Box>

                    {part.manufacturer_name && (
                      <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                        {part.manufacturer_name}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Price and Actions */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                  <Typography sx={{ fontSize: '15px', fontWeight: 600, color: 'text.primary' }}>
                    {part.is_margin_vat_scheme
                      ? formatPrice(part.margin_vat_purchase_price)
                      : formatPrice(showInclVat ? (part.price_incl_vat ?? part.price) : part.price)}
                  </Typography>
                  {part.is_margin_vat_scheme && (
                    <Chip
                      size="small"
                      label={t('Margin VAT')}
                      sx={{ height: 16, fontSize: '9px' }}
                      color="info"
                      variant="outlined"
                    />
                  )}
                  <IconButton
                    onClick={(e) => handleMenuOpen(e, part)}
                    sx={{ color: 'text.disabled', '&:hover': { color: 'text.primary' } }}
                    size="small"
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Paper>
          ))
        )}
      </Box>

      {/* Mobile Pagination Controls */}
      <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
        <PaginationControls
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          loading={loading}
        />
      </Box>

      {/* Context Menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEdit}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          {t('Edit')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setLabelPartIds([menuPart.id]);
            handleMenuClose();
          }}
        >
          <PrintIcon fontSize="small" sx={{ mr: 1 }} />
          {t('partLabels.printLabel')}
        </MenuItem>
        {inventoryEnabled && !menuPart?.stock_info && (
          <MenuItem onClick={handleEnableInventory}>
            <InventoryIcon fontSize="small" sx={{ mr: 1 }} />
            {t('inventory.enableTracking')}
          </MenuItem>
        )}
        {inventoryEnabled && menuPart?.stock_info && (
          <MenuItem onClick={handleDisableInventory}>
            <InventoryIcon fontSize="small" sx={{ mr: 1 }} />
            {t('inventory.disableTracking')}
          </MenuItem>
        )}
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          {t('Delete')}
        </MenuItem>
      </Menu>

      {/* Dialogs */}
      <AddPartDialog
        open={addDialogOpen}
        handleClose={() => setAddDialogOpen(false)}
        mode="registry"
        onPartCreated={handlePartCreated}
      />

      <PartEditDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        part={selectedPart}
        onPartUpdated={handlePartUpdated}
      />

      <PartDetailDialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        part={selectedPart}
        onEdit={() => {
          setDetailDialogOpen(false);
          setEditDialogOpen(true);
        }}
        onPrintLabel={() => setLabelPartIds([selectedPart.id])}
      />

      <PrintPartLabelsDialog
        open={labelPartIds !== null}
        onClose={() => setLabelPartIds(null)}
        partIds={labelDialogPartIds}
      />

      <PartAuditLogDialog
        open={auditLogOpen}
        onClose={() => setAuditLogOpen(false)}
      />

      {inventoryEnabled && (
        <EnableInventoryDialog
          open={enableInventoryDialogOpen}
          onClose={() => setEnableInventoryDialogOpen(false)}
          part={selectedPart}
          onSuccess={refetch}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={cancelDelete}>
        <DialogTitle>{t('Delete part?')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('Are you sure you want to delete "{{name}}"? This action cannot be undone.', { name: partToDelete?.name })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete}>{t('Cancel')}</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            {t('Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default withTranslation()(MainPartsView);
